import { useEffect, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Flex,
    Popover,
    PopoverAnchor,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Spacer,
    StackDivider,
    VStack,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';

export interface MultiSelectOption {
    id: number;
    name: string;
}

const AMultiSelect = ({
    options,
    onChange,
    selectedOptions,
    disabled = false,
}: {
    options: MultiSelectOption[];
    selectedOptions: MultiSelectOption[];
    onChange: (selected: MultiSelectOption[]) => void;
    disabled?: boolean;
}) => {
    const [show, setShow] = useState(false);
    const [selectedData, setSelectedData] = useState<MultiSelectOption[]>([]);
    const [selectedMap, setSelectedMap] = useState<Map<number, number>>(new Map());
    const handleClick = () => setShow(!show);

    const onClickOption = (option: MultiSelectOption) => {
        const optionToAdd = selectedData.find((selected) => selected.id === option.id);
        let newSelectedData: MultiSelectOption[] = [...selectedData];
        const newSelectedMap = new Map();

        if (!optionToAdd) {
            newSelectedData = [...newSelectedData, option];
        } else {
            newSelectedData = newSelectedData.filter((selected) => selected.id !== option.id);
        }

        newSelectedData.forEach((newOption) => newSelectedMap.set(newOption.id, newOption.id));
        setSelectedMap(newSelectedMap);
        setSelectedData(newSelectedData);
        onChange(newSelectedData);
    };

    useEffect(() => {
        if (selectedOptions) {
            const newSelectedMap = new Map();
            selectedOptions.forEach((newOption) => newSelectedMap.set(newOption.id, newOption.id));
            setSelectedMap(newSelectedMap);
            setSelectedData(selectedOptions);
        }
    }, [selectedOptions]);

    return (
        <Box width="100%" zIndex="popover">
            <Popover isLazy lazyBehavior="keepMounted">
                <PopoverAnchor>
                    <Flex width="100%" justifyContent="start" border="1px solid #CAC5C4" borderRadius={4} py={2} px={2}>
                        <Box p="1">
                            {selectedData.map((data) => (
                                <Badge colorScheme="blue" marginRight={2}>
                                    {data && data.name}
                                </Badge>
                            ))}
                        </Box>
                        <Spacer />
                        {!disabled && (
                            <PopoverTrigger>
                                <Box>
                                    <Button h="1.75rem" size="sm" onClick={() => handleClick()}>
                                        {show ? 'Hide' : 'Show'}
                                    </Button>
                                </Box>
                            </PopoverTrigger>
                        )}
                    </Flex>
                </PopoverAnchor>
                <PopoverContent>
                    <PopoverBody>
                        <VStack divider={<StackDivider borderColor="gray.200" />} spacing={1} align="stretch">
                            {options.map((option) => (
                                <Box _hover={{ bg: 'gray.100' }} onClick={() => onClickOption(option)} h="40px">
                                    {option.name} {selectedMap.get(option.id) && <CheckIcon boxSize={6} />}
                                </Box>
                            ))}
                        </VStack>
                    </PopoverBody>
                </PopoverContent>
            </Popover>
        </Box>
    );
};
export default AMultiSelect;
